import {
  FarmEntityRef,
  getFarmPath,
  parseFarmEntityQuery,
} from "portal/utils/routing";
import { Point } from "protos/geo/geo";
import { Selection, setSelection } from "portal/state/farmExplorer";
import { useDispatch } from "react-redux";
import { useEffect, useRef } from "react";
import { useFarmExplorer } from "portal/state/store";
import {
  useLocation,
  useNavigate,
  useNavigationType,
  useParams,
} from "react-router-dom";
import { Zone } from "protos/farm/farm";

const toEntityRef = (selection?: Selection): FarmEntityRef | undefined => {
  if (selection?.type !== "zone" && selection?.type !== "point") {
    return;
  }
  const id = selection.selection.id?.id;
  return id ? { type: selection.type, id } : undefined;
};

const toSelection = (
  entity: FarmEntityRef,
  zonesById: Record<string, Zone>,
  pointsById: Record<string, Point>
): Selection | undefined => {
  if (entity.type === "zone") {
    const zone = zonesById[entity.id];
    return zone && { type: "zone", selection: zone };
  }
  const point = pointsById[entity.id];
  return point && { type: "point", selection: point };
};

/**
 * Keeps the selected zone or point in the URL so it can be linked to.
 *
 * Arriving at a URL (a load, a link, back/forward) selects what it names and
 * calls `onGoToZone` for a zone. Otherwise the selection drives the URL, by
 * replacing it, and those replaces are never read back.
 */
export const useSelectionPath = (
  loadedFarmId: string | undefined,
  zonesById: Record<string, Zone>,
  pointsById: Record<string, Point>,
  onGoToZone: () => void
): void => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { key, pathname, search } = useLocation();
  const { farmId = "" } = useParams();
  const { selection } = useFarmExplorer();
  // the explorer still holds the previous farm until this one is set
  const ready = loadedFarmId === farmId;

  const seenKeyRef = useRef<string>();
  useEffect(() => {
    if (!ready) {
      return;
    }
    const putInUrl = (shown?: Selection): void => {
      const path = getFarmPath(farmId, toEntityRef(shown));
      if (path !== pathname + search) {
        navigate(path, { replace: true });
      }
    };

    const arrived = key !== seenKeyRef.current && navigationType !== "REPLACE";
    seenKeyRef.current = key;
    if (!arrived) {
      putInUrl(selection);
      return;
    }
    // an entity that isn't in this farm selects nothing
    const entity = parseFarmEntityQuery(new URLSearchParams(search));
    const linked = entity && toSelection(entity, zonesById, pointsById);
    if (linked?.type === "zone") {
      onGoToZone();
    }
    dispatch(setSelection(linked));
    putInUrl(linked);
  }, [
    ready,
    key,
    pathname,
    search,
    navigationType,
    farmId,
    selection,
    zonesById,
    pointsById,
    navigate,
    dispatch,
    onGoToZone,
  ]);
};
